import { type DirectTextTurn, settleTurn, useDirectText } from "./use-direct-text";
import { closeAssistantConversation } from "../assistant-conversation-client";
import { assistantThreadKeys, recordVoiceTurns } from "../assistant-threads";
import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import {
  type AgentSessionCredentials,
  parseAgentSessionCredentials,
} from "@/features/voice-agent/agent-session";
import {
  fetchRecentBusinessReceipts,
  mayHaveSaved,
  mayReplaceReceipt,
  receiptRank,
  strongestReceiptRanks,
  withRecentDetail,
} from "@/features/voice-agent/assistant-business-receipts";
import { isLiveSessionActive } from "@/features/voice-agent/voice-session-status";
import {
  useVoiceSession,
  type VoiceSession,
  type VoiceSessionAdapter,
} from "@/lib/voice/use-voice-session";
import type { AssistantToolDisplayResult } from "./assistant-tool-dispatch";
import type { SpokenCaption } from "../speech-presentation";

const SESSION_FUNCTION = "elevenlabs-agent-session";
const UNSUPPORTED_BROWSER_MESSAGE =
  "This browser cannot run the voice agent. Use a recent Chrome, Edge, or Safari release.";
const MAX_VISIBLE_TOOL_RESULTS = 20;
// Rows that may have saved are exempt from the cap above, within this bound.
const MAX_RETAINED_TOOL_RESULTS = 60;
/** A lost live connection never replays a request, so the operator reconciles first. */
export const DISCONNECT_RECOVERY_MESSAGE =
  "Nothing was retried. Check recent business commands and document receipts before asking again.";

/**
 * A newer outcome for the same command replaces the older row, so one command
 * never shows contradictory receipts. A repeat question is its own command: a
 * confirmed repeat gets a new id and never clears another command's question.
 * A document has no command: for the same proposal revision, a successful
 * execute settles an unconfirmed row and another unconfirmed attempt folds into
 * it. A rejection does not, since the document the unconfirmed attempt created
 * can be what the rejection reports.
 */
function supersedes(next: AssistantToolDisplayResult, earlier: AssistantToolDisplayResult) {
  if (earlier.id === next.id) return true;
  if (earlier.unconfirmedDocument && next.proposal && earlier.proposal)
    return (
      (next.status === "success" || next.unconfirmedDocument === true) &&
      next.proposal.id === earlier.proposal.id &&
      next.proposal.revision === earlier.proposal.revision
    );
  const [now, before] = [next.receipt, earlier.receipt];
  return Boolean(now && before && now.commandId === before.commandId);
}

/**
 * A row the operator must reconcile before asking again: a command that may
 * have saved, or a document execute whose outcome is not confirmed (a document
 * has no command receipt, so the dispatcher marks its row instead).
 */
function rowMayHaveSaved(row: AssistantToolDisplayResult): boolean {
  return row.receipt ? mayHaveSaved(row.receipt) : row.unconfirmedDocument === true;
}

/**
 * Keeps the newest rows up to the visible cap, dropping the oldest first. A
 * row that may have saved is dropped last: it is the evidence the operator
 * reconciles, and twenty later lookups must not push it off the screen.
 */
function capRows(rows: AssistantToolDisplayResult[]): AssistantToolDisplayResult[] {
  let excess = rows.length - MAX_VISIBLE_TOOL_RESULTS;
  const kept = rows.filter((row) => {
    if (excess <= 0 || rowMayHaveSaved(row)) return true;
    excess -= 1;
    return false;
  });
  return kept.slice(-MAX_RETAINED_TOOL_RESULTS);
}

/**
 * A row for a command is replaced only by a receipt at least as definitive: a
 * refusal that says an attempt did not run never hides an earlier row saying
 * the command may have saved.
 */
function holdsStrongerRow(rows: AssistantToolDisplayResult[], next: AssistantToolDisplayResult) {
  const receipt = next.receipt;
  return Boolean(
    receipt &&
    rows.some(
      (row) =>
        row.id !== next.id &&
        row.receipt?.commandId === receipt.commandId &&
        !mayReplaceReceipt(receipt, row.receipt),
    ),
  );
}

export type UseElevenLabsAgentReturn = Omit<VoiceSession, "stop"> & {
  spokenCaption?: SpokenCaption | null;
  conversationId?: string | null;
  stop: () => Promise<void>;
  textConversationId?: string | null;
  toolResults: AssistantToolDisplayResult[];
  mode?: "text" | "live";
  textPending?: boolean;
  /** The text thread the next message continues; null starts a new one. */
  threadId?: string | null;
  resumeThread?: (threadId: string, turns: DirectTextTurn[]) => Promise<boolean>;
  newThread?: () => Promise<boolean>;
  /** Drops the current thread without closing anything; the next message starts a new one. */
  forgetThread?: () => void;
  /** Set once the live call's turns can no longer be stored in the thread. */
  voiceNotice?: string;
};

export const VOICE_TURNS_NOT_SAVED =
  "This call is no longer being saved to the thread (calls are saved for 15 minutes). End it and start a new call to keep saving.";

function isAgentSupported(): boolean {
  const capture: MediaDevices | undefined = globalThis.navigator?.mediaDevices;
  return (
    typeof capture?.getUserMedia === "function" &&
    typeof globalThis.AudioContext === "function" &&
    typeof globalThis.WebSocket === "function"
  );
}

/** The mint's "Thread unavailable" refusal, as opposed to a staff-access denial. */
function namesThreadRefusal(error: unknown): boolean {
  if (typeof error !== "object" || error === null) return false;
  const { code, error: body, message } = error as Record<string, unknown>;
  return [code, body, message].some((value) => value === "Thread unavailable");
}

/**
 * Runs one ElevenLabs Agents conversation. Transport, PCM capture, playback,
 * and provider event handling stay out of the shell bundle until the operator
 * starts a session.
 */
export function useElevenLabsAgent(actorId: string | null = null): UseElevenLabsAgentReturn {
  const text = useDirectText(actorId);
  const { adoptThread } = text;
  const queryClient = useQueryClient();
  // The thread the next call continues, read when the call is minted.
  const currentThread = useRef(text.threadId);
  const mintedThread = useRef<string | null>(null);
  const [mode, setMode] = useState<"text" | "live">("text");
  const modeRef = useRef<"text" | "live">("text");
  const liveReference = useRef<string | null>(null);
  const pendingRevocations = useRef(new Set<string>());
  const [liveConversationId, setLiveConversationId] = useState<string | null>(null);
  const transition = useRef(false);
  const mounted = useRef(true);
  const cancelStart = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      cancelStart.current = true;
    };
  }, []);
  const [transitionError, setTransitionError] = useState<string>();
  const [voiceTurnsRefused, setVoiceTurnsRefused] = useState(false);
  const [spokenCaption, setSpokenCaption] = useState<SpokenCaption | null>(null);
  const [history, setHistory] = useState<import("@/lib/voice/conversation").VoiceTurn[]>([]);
  const [toolResults, setToolResults] = useState<AssistantToolDisplayResult[]>([]);
  const turnsRef = useRef<import("@/lib/voice/conversation").VoiceTurn[]>([]);

  // A command that may have saved is resolved by reading its stored status,
  // never inferred: a saved or rejected command replaces its row, while a
  // command still `ready` leaves the row as it is (an attempt still executing
  // also reads as ready). A failed read changes nothing.
  const resolveUncertain = useCallback(async () => {
    const recent = await fetchRecentBusinessReceipts(liveReference.current);
    if (!recent || !mounted.current) return;
    setToolResults((current) =>
      current.map((row) => {
        if (!row.receipt || !mayHaveSaved(row.receipt)) return row;
        const [resolved] = withRecentDetail([row.receipt], recent);
        return resolved === row.receipt ? row : { ...row, receipt: resolved };
      }),
    );
  }, []);
  const addToolResult = useCallback(
    (result: AssistantToolDisplayResult) => {
      setToolResults((current) =>
        holdsStrongerRow(current, result)
          ? current
          : capRows([...current.filter((item) => !supersedes(result, item)), result]),
      );
      if (result.receipt && mayHaveSaved(result.receipt)) void resolveUncertain();
    },
    [resolveUncertain],
  );
  // A text report at least as definitive as a voice row for the same command
  // (its own receipts or its recovery read) belongs to the text transcript, so
  // the voice row would only contradict it. A weaker one, such as a text
  // attempt that did not run, leaves the voice row standing.
  const outlastedBy = (textRanks: Map<string, number>, { receipt }: AssistantToolDisplayResult) =>
    receipt !== undefined && (textRanks.get(receipt.commandId) ?? -1) >= receiptRank(receipt);
  // The row is dropped for good: once the text turns are archived or cleared
  // their report is no longer visible here, and the voice row must not return.
  // Applied while rendering, keyed on the text turns, so a row never paints
  // beside the text report that outlasts it.
  const [reconciledTextTurns, setReconciledTextTurns] = useState(text.turns);
  if (reconciledTextTurns !== text.turns) {
    setReconciledTextTurns(text.turns);
    const textRanks = strongestReceiptRanks(text.turns);
    if (textRanks.size) {
      const kept = toolResults.filter((row) => !outlastedBy(textRanks, row));
      if (kept.length !== toolResults.length) setToolResults(kept);
    }
  }
  // What stays on screen once the call ends: its command receipts and any
  // document execute that may have created its document, not its lookups or the
  // repeat questions only the live agent could hear answered.
  const receiptsAfterCall = () =>
    toolResults.filter((row) =>
      row.receipt ? row.receipt.status !== "needs_input" : row.unconfirmedDocument === true,
    );
  const remember = useCallback((turns: typeof history) => {
    if (!turns.length) return;
    setHistory((previous) => [
      ...previous,
      ...turns.map((turn) => ({
        ...settleTurn(turn),
        id: `${crypto.randomUUID()}-${turn.id}`,
        text: turn.isFinal ? turn.text : "Response interrupted.",
      })),
    ]);
  }, []);
  const revokeReference = useCallback((conversationId: string) => {
    pendingRevocations.current.add(conversationId);
    return closeAssistantConversation(conversationId).then(() => {
      pendingRevocations.current.delete(conversationId);
      if (liveReference.current !== conversationId) return;
      liveReference.current = null;
      if (mounted.current) setLiveConversationId(null);
    });
  }, []);
  const adapter = useMemo<VoiceSessionAdapter<AgentSessionCredentials>>(
    () => ({
      connect: async (input) => {
        const { connectElevenLabsAgent } = await import("./elevenlabs-agent-transport");
        let voiceId: string | undefined;
        try {
          voiceId = localStorage.getItem("xan.voice.voice_id")?.trim() || undefined;
        } catch {
          // ignore
        }

        const priorTurns = turnsRef.current;
        const contextHistory = priorTurns
          .filter((t) => t.isFinal && t.text)
          .map((t) => ({
            role: (t.role === "operator" ? "operator" : "assistant") as "operator" | "assistant",
            text: t.text,
          }));

        // Each finalized turn is stored in the call's thread; a stored turn
        // refreshes every view of the operator's threads.
        setVoiceTurnsRefused(false);
        setSpokenCaption(null);
        const storeTurn = recordVoiceTurns(
          input.credentials.conversationId,
          () => void queryClient.invalidateQueries({ queryKey: assistantThreadKeys.all(actorId) }),
          // Said once, so the rest of the call is not lost silently; a call that
          // has since ended or been replaced does not mark the current one.
          () => {
            if (liveReference.current === input.credentials.conversationId)
              setVoiceTurnsRefused(true);
          },
        );
        return connectElevenLabsAgent({
          ...input,
          onFinalTurn: storeTurn,
          onCaption: (caption) => {
            if (mounted.current) setSpokenCaption(caption);
          },
          voiceId,
          contextHistory: contextHistory.length > 0 ? contextHistory : undefined,
          controls: {
            ...input.controls,
            endConnection: () => {
              input.controls.endConnection();
              remember(input.controls.read().turns);
              liveReference.current = null;
              if (mounted.current) setLiveConversationId(null);
              modeRef.current = "text";
              setMode("text");
            },
          },
          onToolResult: addToolResult,
        });
      },
      disconnectGuidance: DISCONNECT_RECOVERY_MESSAGE,
      functionName: SESSION_FUNCTION,
      incompleteSessionMessage:
        "Assistant backend update required. Live voice needs an updated backend response.",
      isSupported: isAgentSupported,
      parseCredentials: parseAgentSessionCredentials,
      sessionRequest: () => {
        mintedThread.current = currentThread.current;
        return mintedThread.current ? { threadId: mintedThread.current } : {};
      },
      // A thread the mint refuses (400 malformed; 403 gone or not this
      // operator's) is forgotten, unless the operator has moved on to another,
      // so the next start opens a new one. An outage keeps it for the retry.
      mintFailed: (status, error) => {
        const refused = mintedThread.current;
        // A 403 also answers a caller without staff access; only the thread's
        // own refusal forgets it.
        const threadRefused = status === 400 || (status === 403 && namesThreadRefusal(error));
        if (threadRefused && refused && currentThread.current === refused) adoptThread(null);
      },
      claimCredentials: (credentials) => {
        liveReference.current = credentials.conversationId;
        if (mounted.current && !cancelStart.current) {
          setLiveConversationId(credentials.conversationId);
          // Text and voice share the thread: the next typed message continues it.
          adoptThread(credentials.threadId);
        }
      },
      releaseCredentials: (credentials) => {
        void revokeReference(credentials.conversationId).catch(() => undefined);
      },
      sessionFailureMessage: "Could not start a voice agent session.",
      unsupportedMessage: UNSUPPORTED_BROWSER_MESSAGE,
    }),
    [actorId, addToolResult, adoptThread, queryClient, remember, revokeReference],
  );

  const session = useVoiceSession(adapter);
  const { start: startSession, sendText: sendSessionText, stop: stopSession } = session;
  const closeLiveReference = async () => {
    const references = new Set(pendingRevocations.current);
    if (liveReference.current) references.add(liveReference.current);
    // Begin each independent revocation together, reusing the client's in-flight
    // close promises. A failed old mint stays tracked without replacing the live ID.
    await Promise.all([...references].map(revokeReference));
  };
  // Returns false when unmount or cancellation raced the awaited close.
  const archivePreviousTurns = async () => {
    if (modeRef.current === "live") {
      remember(session.turns);
      return true;
    }
    await text.close();
    if (!mounted.current || cancelStart.current) return false;
    remember(text.turns);
    text.clear();
    return true;
  };
  const abandonStartedSession = async () => {
    stopSession();
    await closeLiveReference();
    modeRef.current = "text";
    if (mounted.current) setMode("text");
  };
  const start = async () => {
    if (transition.current || isLiveSessionActive(session.status)) return;
    transition.current = true;
    cancelStart.current = false;
    setTransitionError(undefined);
    // A new call starts unrefused; the previous call's notice must not show while it connects.
    setVoiceTurnsRefused(false);
    try {
      await closeLiveReference();
      if (!mounted.current || cancelStart.current) return;
      const textRanks = strongestReceiptRanks(text.turns);
      if (!(await archivePreviousTurns())) return;
      // A command or document that may have saved stays visible into the next
      // call until a newer outcome replaces it, so a reconnect cannot hide the
      // need to check it. Everything else belongs to the call that ended.
      setToolResults((current) =>
        current.filter((row) => rowMayHaveSaved(row) && !outlastedBy(textRanks, row)),
      );
      modeRef.current = "live";
      setMode("live");
      await startSession();
      if (!mounted.current || cancelStart.current) await abandonStartedSession();
    } catch {
      setTransitionError("The previous conversation could not be closed. Please retry.");
    } finally {
      transition.current = false;
    }
  };
  const stop = async () => {
    if (transition.current) {
      cancelStart.current = true;
      stopSession();
      return;
    }
    transition.current = true;
    setTransitionError(undefined);
    try {
      if (modeRef.current === "live") {
        stopSession();
        await closeLiveReference();
        remember(session.turns);
      } else {
        await text.close();
        remember(text.turns);
        text.clear();
      }
      modeRef.current = "text";
      setMode("text");
    } catch {
      setTransitionError("The previous conversation could not be closed. Please retry.");
    } finally {
      transition.current = false;
    }
  };
  const sendText = async (content: string) => {
    if (transition.current || transitionError) return false;
    if (modeRef.current === "text") return text.sendText(content);
    // An ended or failed live session never restarts implicitly on Send.
    if (session.status !== "listening" && session.status !== "speaking") return false;
    return sendSessionText(content);
  };
  // Switching threads ends the open conversation, text or live, and shows only
  // the chosen thread. A row that may have saved stays until it is reconciled.
  const switchThread = async (change: () => Promise<boolean>) => {
    // Switching would cancel a pending reply; Cancel is the explicit way to do that.
    // The live flag, not the rendered state, which can lag a send that just settled.
    if (transition.current || text.isReplying()) return false;
    transition.current = true;
    setTransitionError(undefined);
    try {
      if (modeRef.current === "live") {
        stopSession();
        await closeLiveReference();
        modeRef.current = "text";
        setMode("text");
      }
      if (!(await change())) return false;
      setHistory([]);
      setToolResults((current) => current.filter(rowMayHaveSaved));
      return true;
    } catch {
      setTransitionError("The previous conversation could not be closed. Please retry.");
      return false;
    } finally {
      transition.current = false;
    }
  };
  const toggle = () => {
    void (isLiveSessionActive(session.status) ? stop() : start());
  };
  const activeTurns = mode === "text" ? text.turns : session.turns;
  const turns = useMemo(() => [...history, ...activeTurns], [history, activeTurns]);
  useLayoutEffect(() => {
    turnsRef.current = turns;
    currentThread.current = text.threadId;
  }, [turns, text.threadId]);
  const errorMessage =
    transitionError ?? (mode === "text" ? text.errorMessage : session.errorMessage);
  return {
    ...session,
    spokenCaption: mode === "live" && !session.outputMuted ? spokenCaption : null,
    status: errorMessage ? "error" : mode === "text" ? "idle" : session.status,
    errorMessage,
    turns,
    sendText,
    start,
    stop,
    textConversationId: mode === "text" ? text.conversationId : null,
    conversationId: mode === "text" ? text.conversationId : liveConversationId,
    toggle,
    mode,
    textPending: text.pending,
    voiceNotice: mode === "live" && voiceTurnsRefused ? VOICE_TURNS_NOT_SAVED : undefined,
    threadId: text.threadId,
    resumeThread: (threadId: string, turns: DirectTextTurn[]) =>
      switchThread(() => text.resumeThread(threadId, turns)),
    newThread: () => switchThread(text.newThread),
    forgetThread: text.forgetThread,
    toolResults: mode === "live" ? toolResults : receiptsAfterCall(),
  } as UseElevenLabsAgentReturn;
}
